import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { API } from '../lib/api';
// The leaf module, NOT `useMailDrafts` — that one imports `AuthContext`, whose
// module scope calls `axios.create()`, and this hook has no business dragging
// the auth stack onto the compose routes.
import { invalidateDrafts } from './mailDraftCache';

/**
 * Composer drafts: debounced autosave to the SERVER, a visible saved-at time,
 * Discard, and the beforeunload guard.
 *
 * ## What changed, and why all three symptoms were one bug
 *
 * This wrote to **localStorage**, and `restore()` repopulated the composer on
 * open. That produced every part of the report at once:
 *
 *   * the rail could not count drafts — the server had never seen one;
 *   * the Drafts view listed nothing, for the same reason;
 *   * and "compose should always be a new compose state" was false, because
 *     opening compose restored the last draft into it.
 *
 * "Drafts should be saved on the Drafts page rather than on the compose page"
 * is the fix for all three: the draft moves off the composer and into a place
 * you can go and look at. A localStorage draft was also invisible from any
 * other device and died with the browser profile, which is not what anyone
 * means by "saved".
 *
 * **There is deliberately no `restore()` any more.** Compose opens empty,
 * always. A draft is reopened by clicking it in the Drafts view, which hands
 * it over in router state — an explicit act, not a surprise.
 *
 * ## Saving is best-effort and never blocks typing
 *
 * A failed autosave is logged to the caller through `error` and otherwise
 * ignored: losing a keystroke to a network blip is worse than a stale draft,
 * and the send path does not read the draft at all.
 *
 * ## A save is not finished until the surfaces that show it know
 *
 * The draft landed on the server and NOTHING told the Drafts list or the
 * folder rail's badge, so both kept serving their caches: the list until its
 * 60s poll came round, the badge — which has no poll — indefinitely. See
 * `hooks/useMailDrafts.invalidateDrafts` for why that produced exactly the two
 * symptoms reported, in exactly that order.
 *
 * @param {object} value the current draft payload
 * @param {object} opts  `{ enabled, inboxId, draftId, getAuthHeader }`
 */
export function useComposerDraft(
    value,
    { enabled = true, inboxId = null, draftId = null, getAuthHeader } = {},
) {
    const [savedAt, setSavedAt] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [error, setError] = useState(null);
    const queryClient = useQueryClient();
    // The server's id for this draft: null until the first save, then reused
    // so autosave UPDATES one row instead of inserting one per keystroke
    // burst.
    const idRef = useRef(draftId);
    // The value is re-created every render; a ref keeps the debounce effect
    // from restarting on every keystroke while still writing the LATEST value.
    const latest = useRef(value);
    latest.current = value;

    // An attachment is content. Without it here, attaching a file and
    // nothing else left the composer "clean", so the draft was never saved
    // and the upload sat orphaned in S3 with nothing pointing at it. The
    // server's `mail_drafts.is_empty` agrees, for the same reason.
    const hasContent = useCallback((v) => Boolean(
        v && (
            (v.to && v.to.length) || (v.cc && v.cc.length) || (v.bcc && v.bcc.length)
            || v.subject || stripEmptyHtml(v.body)
            || (v.attachments && v.attachments.length)
        )
    ), []);

    useEffect(() => {
        if (!enabled) return;
        if (hasContent(value)) setIsDirty(true);
    }, [value, enabled, hasContent]);

    useEffect(() => {
        if (!enabled || !isDirty) return undefined;
        const timer = setTimeout(async () => {
            const v = latest.current || {};
            try {
                const res = await axios.post(`${API}/mail/drafts`, {
                    draft_id: idRef.current,
                    inbox_id: inboxId,
                    to: addresses(v.to),
                    cc: addresses(v.cc),
                    bcc: addresses(v.bcc),
                    subject: v.subject || '',
                    body: v.body || '',
                    from_address: v.from_address || null,
                    attachments: v.attachments || [],
                }, { headers: getAuthHeader?.() || {} });
                idRef.current = res?.data?.data?.draft_id ?? idRef.current;
                setSavedAt(new Date());
                setError(null);
                // The Drafts list and the rail's badge are caches of what was
                // just written. Neither is mounted on a compose route, so this
                // only marks them stale and the refetch happens on the
                // navigation that asks to see them.
                invalidateDrafts(queryClient, inboxId);
            } catch (e) {
                // Never interrupt typing. The composer keeps the content in
                // component state either way, and the send path does not read
                // the draft.
                setError(e);
            }
            setIsDirty(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [isDirty, enabled, inboxId, getAuthHeader, queryClient]);

    useEffect(() => {
        const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    /** Drop the stored draft — after a successful send, or on Discard. */
    const clear = useCallback(async () => {
        const id = idRef.current;
        idRef.current = null;
        setSavedAt(null);
        setIsDirty(false);
        if (!id) return;
        try {
            await axios.delete(`${API}/mail/drafts/${encodeURIComponent(id)}`, {
                params: inboxId ? { inbox_id: inboxId } : undefined,
                headers: getAuthHeader?.() || {},
            });
        } catch {
            // A draft that outlives its message is untidy, not harmful.
        }
        // Outside the catch: the row is gone from the list either way. If the
        // DELETE failed the refetch puts it back, which is the truth; if it
        // succeeded and this were skipped, Discard would leave the badge
        // counting a draft that no longer exists — the same stale-cache defect
        // as the save path, in the other direction.
        invalidateDrafts(queryClient, inboxId);
    }, [inboxId, getAuthHeader, queryClient]);

    /**
     * Drop the stored draft and tell the caller it is gone.
     *
     * The CONFIRMATION moved out to the pages, which is where UI belongs: it
     * used to be a `window.confirm` in here — the browser's grey
     * "<host> says" alert — and a hook has no business rendering a dialog,
     * which is exactly why it reached for the one primitive that needs no
     * component (#708). The pages have the app's `AlertDialog` and use it for
     * every other destructive confirmation.
     *
     * `hasDraftContent` below is the guard that survived the move, unchanged
     * in meaning: an empty composer is discarded without asking.
     */
    const discard = useCallback((onConfirmed) => {
        clear();
        onConfirmed?.();
        return true;
    }, [clear]);

    return {
        savedAt,
        // "Draft saved at 14:32" — a time, not a relative string, so it does
        // not need a ticking re-render to stay true.
        savedAtLabel: savedAt
            ? savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : null,
        isDirty,
        error,
        draftId: idRef.current,
        /** Is there anything to lose? Decides whether Discard confirms. */
        hasDraftContent: hasContent(latest.current),
        clear,
        discard,
    };
}

/**
 * A rich-text editor emits `<p></p>` or `<br>` for an untouched field, so a
 * length test on the raw HTML calls every empty composer dirty — and would
 * autosave a blank draft the moment compose opened.
 */
export function stripEmptyHtml(body) {
    return (body || '')
        .replace(/<p>\s*<\/p>/gi, '')
        .replace(/<br\s*\/?>/gi, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
}

/** Recipients arrive as strings from one composer and `{email}` from another. */
export function addresses(list) {
    return (list || [])
        .map((entry) => (typeof entry === 'string' ? entry : entry?.email))
        .filter(Boolean);
}

export default useComposerDraft;
